import * as THREE from "three";
import { Skybox } from "../../sky/components/Skybox.js";
import { Clouds } from "../../sky/components/Clouds.js";
import { Stars } from "../../sky/components/Stars.js";
import { Cityscape } from "../../sky/components/Cityscape.js";
import { createLensflareTextures } from "../../sky/utils.js";
import { setSkyStencilTest } from "../../sky/skyMask.js";

// Sky, outdoor decorations and time-of-day fill, installed on SceneManager.prototype.
export const skyMethods = {
  createDynamicSky() {
    const lensflareTextures = createLensflareTextures();
    this.skybox = new Skybox(lensflareTextures);

    this.skybox.onTimeOfDayChanged = (newTimeOfDay, elapsedTime) => {
      this.timeOfDay = newTimeOfDay;
      this._applyTimeOfDayToDecorations(newTimeOfDay);
      this.updateAmbientLighting(newTimeOfDay);
    };

    this.scene.add(this.skybox);
    this.scene.add(this.skybox.sun);
    this.scene.add(this.skybox.sun.target);

    // Clouds/stars/cityscape are outdoor-only; the default white studio has the sky disabled, so they're built lazily on the first setSkyVisible(true) to stay off the initial-load path.
  },

  /**
   * Lazily build the outdoor sky decorations (clouds, stars, cityscape). Idempotent —
   * only the first call constructs them; later calls are no-ops. Called when the sky is
   * first made visible so the common white-room load never pays for them.
   */
  ensureSkyDecorations() {
    if (this.clouds) return;

    this.clouds = new Clouds();
    this.scene.add(this.clouds);

    this.stars = new Stars();
    this.scene.add(this.stars);

    this.cityscape = new Cityscape();
    // Laid out for a viewer on -X; the viewer is on +Z.
    this.cityscape.rotation.y = Math.PI / 2;
    // The tracer drops it for views from outside the room.
    this.cityscape.userData.isOutdoors = true;
    this.scene.add(this.cityscape);

    // Sync to the current time of day (they missed the changes fired before creation).
    if (this.timeOfDay) this._applyTimeOfDayToDecorations(this.timeOfDay);
  },

  /** With walls up, the sky and everything outdoors draws only through window glass. */
  setSkyMasked(masked) {
    for (const root of [this.skybox, this.clouds, this.stars, this.cityscape]) {
      root?.traverse((object) => {
        if (!object.material) return;
        for (const material of Array.isArray(object.material) ? object.material : [object.material]) {
          setSkyStencilTest(material, masked);
        }
      });
    }
    const flareProbe = this.skybox?.lensflare?.occlusionMaterial;
    if (flareProbe) setSkyStencilTest(flareProbe, masked);
  },

  _applyTimeOfDayToDecorations(timeOfDay) {
    if (this.clouds) {
      if (timeOfDay === "Nighttime") this.clouds.setCloudColor(new THREE.Color(0.1, 0.1, 0.2));
      else if (timeOfDay === "Sunrise") this.clouds.setCloudColor(new THREE.Color(0.8, 0.4, 0.4));
      else if (timeOfDay === "Sunset") this.clouds.setCloudColor(new THREE.Color(0.8, 0.3, 0.3));
      else this.clouds.setCloudColor(new THREE.Color(1.0, 1.0, 1.0));
    }
    if (this.stars) this.stars.setVisible(timeOfDay === "Nighttime");

    if (this.cityscape && this.skybox && this.skybox.sun) {
      const sunDirection = new THREE.Vector3();
      this.skybox.sun.getWorldDirection(sunDirection);
      this.cityscape.update(timeOfDay, sunDirection);
    }
  },

  updateAmbientLighting(timeOfDay) {
    // In white_room, studio lights manage ambient — don't override the hemisphere
    if (this.environmentManager?.studioLights) return;

    // Fill lights (hemisphere + ambient) re-illuminate surfaces that are in
    // shadow, so scaling them down deepens cast (VSM) shadows. Lower
    // fillLightFactor = darker shadows (and a slightly dimmer scene overall).
    const fill = this.fillLightFactor;

    if (timeOfDay === "Nighttime") {
      this.hemisphereLight.intensity = 0.3 * fill;
      this.hemisphereLight.color.setHex(0x4a5f8f);
      this.hemisphereLight.groundColor.setHex(0x1a1410);
    } else if (timeOfDay === "Sunrise") {
      this.hemisphereLight.intensity = 0.5 * fill;
      this.hemisphereLight.color.setHex(0xffb380);
      this.hemisphereLight.groundColor.setHex(0x362d1a);
    } else if (timeOfDay === "Sunset") {
      this.hemisphereLight.intensity = 0.5 * fill;
      this.hemisphereLight.color.setHex(0xff8050);
      this.hemisphereLight.groundColor.setHex(0x362d1a);
    } else {
      this.hemisphereLight.intensity = 0.6 * fill;
      this.hemisphereLight.color.setHex(0x87ceeb);
      this.hemisphereLight.groundColor.setHex(0x362d1a);
    }

    if (this.skybox && this.skybox.ambientLight) {
      this.skybox.ambientLight.intensity = 0.3 * fill;
    }

    if (this.lastEnvUpdateTime !== timeOfDay) {
      this.updateEnvironmentMap();
      this.lastEnvUpdateTime = timeOfDay;
    }
  },

  setSkyVisible(visible) {
    // Build the outdoor decorations on first reveal; a white room never triggers this.
    if (visible) this.ensureSkyDecorations();

    if (this.skybox) {
      this.skybox.visible = visible;
      if (this.skybox.sun) this.skybox.sun.visible = visible;
      if (this.skybox.lensflare) this.skybox.lensflare.visible = visible;
    }
    if (this.clouds) this.clouds.visible = visible;
    if (this.stars) this.stars.visible = visible;
    if (this.cityscape) this.cityscape.visible = visible;
  },

  _updateSky(deltaTime, camera) {
    if (this.skybox && this.timeManager && camera) {
      this._roomCenter ??= new THREE.Vector3();
      this.skybox.update(
        this.timeManager.getTime(),
        performance.now() / 1000,
        this._roomCenter,
        camera
      );
    }

    if (this.clouds && camera) {
      this.clouds.update(deltaTime, camera.position);
    }

    if (this.stars) {
      this.stars.update(performance.now() / 1000);
    }
  },
};
